/**
 * AgentCard - Individual AI agent status card
 * Shows agent icon, name, status, and progress
 */

import React from 'react';
import { AgentStatus, AgentStatusType } from './types';
import { CheckCircle, Loader2, AlertCircle, Clock, User, Pause } from 'lucide-react';

interface AgentCardProps {
  agent: AgentStatus;
  compact?: boolean;
  onClick?: () => void;
}

const statusConfig: Record<AgentStatusType, { color: string; bgColor: string; label: string; Icon: React.ElementType }> = {
  idle: { color: 'text-slate-400', bgColor: 'bg-slate-50', label: 'Idle', Icon: Pause },
  queued: { color: 'text-slate-500', bgColor: 'bg-slate-50', label: 'Queued', Icon: Clock },
  running: { color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Running', Icon: Loader2 },
  complete: { color: 'text-emerald-600', bgColor: 'bg-emerald-50', label: 'Complete', Icon: CheckCircle },
  error: { color: 'text-red-600', bgColor: 'bg-red-50', label: 'Error', Icon: AlertCircle },
  waiting_human: { color: 'text-amber-600', bgColor: 'bg-amber-50', label: 'Needs Review', Icon: User }
};

export default function AgentCard({ agent, compact = false, onClick }: AgentCardProps) {
  const config = statusConfig[agent.status];
  const { Icon } = config;
  
  if (compact) {
    return (
      <div 
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${config.bgColor} ${config.color} cursor-pointer hover:opacity-80 transition-opacity`}
        onClick={onClick}
        title={`${agent.agentName}: ${config.label}`}
      >
        <span>{agent.icon}</span>
        {agent.status === 'running' && (
          <Loader2 className="w-3 h-3 animate-spin" />
        )}
        {agent.progress && (
          <span className="font-medium">{agent.progress.percentage}%</span>
        )}
      </div>
    );
  }
  
  return (
    <div 
      className={`
        relative p-4 rounded-lg border-2 transition-all duration-300 cursor-pointer
        ${agent.status === 'running' 
          ? 'border-blue-400 shadow-lg shadow-blue-100 animate-pulse-subtle' 
          : 'border-slate-200 hover:border-slate-300'
        }
        ${config.bgColor}
      `}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{agent.icon}</span>
          <div>
            <h4 className="font-medium text-slate-900 text-sm leading-tight">
              {agent.agentName.replace(' AI', '')}
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">{agent.persona.slice(0, 35)}...</p>
          </div>
        </div>
        <div className={`flex items-center gap-1 ${config.color}`}>
          <Icon className={`w-4 h-4 ${agent.status === 'running' ? 'animate-spin' : ''}`} />
        </div>
      </div>
      
      {/* Status label */}
      <div className={`text-xs font-medium ${config.color} mb-2`}>
        {config.label}
        {agent.status === 'running' && agent.progress && (
          <span className="ml-1">({agent.progress.current}/{agent.progress.total})</span>
        )}
      </div>
      
      {/* Progress bar */}
      {agent.progress && agent.status === 'running' && (
        <div className="mt-2">
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${agent.progress.percentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>{agent.progress.current} of {agent.progress.total}</span>
            <span>{agent.progress.percentage}%</span>
          </div>
        </div>
      )}
      
      {/* Result summary */}
      {agent.result && agent.status === 'complete' && (
        <p className="text-xs text-slate-600 mt-2 line-clamp-2">
          {agent.result.summary}
        </p>
      )}
      
      {/* Items needing review badge */}
      {agent.result?.itemsNeedingReview && agent.result.itemsNeedingReview > 0 && (
        <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
          <User className="w-3 h-3" />
          {agent.result.itemsNeedingReview} need review
        </div>
      )}
    </div>
  );
}
